import { apiRequest, buildQueryString, PaginationResponse } from '../config/api';
import { WeeklySummaryData } from '../types/weekly';

export interface WeeklySummaryFilters {
  page?: number;
  limit?: number;
  year?: number;
  week?: number;
}

export interface WeeklySummaryApi {
  id: number;
  title: string;
  summary: string;
  key_themes: string | string[] | null;
  week_start_date: string;
  week_end_date: string;
  year: number;
  week_number: number;
  total_article_count: number;
  daily_summary_count: number;
  featured: number | boolean | null;
  view_count: number;
  created_at: string;
  updated_at: string;
}

const parseKeyThemes = (themes: string | string[] | null | undefined): string[] => {
  if (!themes) return [];
  if (Array.isArray(themes)) return themes;
  try {
    const parsed = JSON.parse(themes);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const normalizeWeeklySummary = (summary: WeeklySummaryApi): WeeklySummaryData => ({
  id: summary.id,
  title: summary.title,
  summary: summary.summary,
  key_themes: parseKeyThemes(summary.key_themes),
  week_start_date: summary.week_start_date,
  week_end_date: summary.week_end_date,
  year: summary.year,
  week_number: summary.week_number,
  total_article_count: summary.total_article_count,
  daily_summary_count: summary.daily_summary_count,
  featured: Boolean(summary.featured),
  view_count: summary.view_count,
  created_at: summary.created_at,
  updated_at: summary.updated_at,
});

export const weeklySummaryService = {
  // Get all weekly summaries with pagination and filtering
  getWeeklySummaries: async (
    filters: WeeklySummaryFilters = {}
  ): Promise<PaginationResponse<WeeklySummaryData>> => {
    const queryString = buildQueryString(filters);
    const response = await apiRequest<PaginationResponse<WeeklySummaryApi>>(
      `/api/weekly-summaries${queryString}`
    );
    return {
      data: response.data.map(normalizeWeeklySummary),
      pagination: response.pagination,
    };
  },

  // Get a specific weekly summary by ID
  getWeeklySummaryById: async (id: number): Promise<WeeklySummaryData> => {
    const response = await apiRequest<{ data: WeeklySummaryApi }>(`/api/weekly-summaries/${id}`);
    if (!response.data) {
      throw new Error('Weekly summary not found');
    }
    return normalizeWeeklySummary(response.data);
  },

  // Get weekly summary by week number and year
  getWeeklySummaryByWeek: async (year: number, week: number): Promise<WeeklySummaryData> => {
    const response = await apiRequest<{ data: WeeklySummaryApi }>(
      `/api/weekly-summaries/week/${year}/${week}`
    );
    if (!response.data) {
      throw new Error('Weekly summary not found');
    }
    return normalizeWeeklySummary(response.data);
  },
};
